<template>
  <div>
    <!-- Header with back button + family name -->
    <div class="flex items-center gap-3 mb-5">
      <button
        @click="$emit('back')"
        class="link-cyan flex items-center gap-1 text-sm font-medium"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ $t('common.back') }}
      </button>
      <h3 class="text-lg font-bold text-foreground">
        {{ familyName }} — {{ $t('family_detail_report.title') }}
      </h3>
    </div>

    <div class="grid gap-5">
      <!-- Vue d'ensemble de la famille -->
      <ReportCard :title="$t('family_detail_report.overview')">
        <div v-if="familySummary" class="space-y-4">
          <!-- Métriques principales -->
          <div class="grid grid-cols-2 gap-3">
            <!-- Revenue Card -->
            <div class="p-3 rounded-xl product-row">
              <div class="flex items-center justify-between mb-1">
                <div class="w-6 h-6 bg-cyan-500/10 rounded flex items-center justify-center">
                  <svg class="w-3 h-3 icon-cyan" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                      clip-rule="evenodd" />
                  </svg>
                </div>
                <div v-if="familySummary?.comparison"
                  class="text-xs font-medium"
                  :class="getRevenueEvolutionClass()">
                  {{ getRevenueEvolution() }}
                </div>
              </div>
              <div>
                <div class="text-lg font-bold text-foreground">
                  {{ formatCurrency(familySummary.tax_data[taxKey]) }}
                </div>
                <div class="text-xs text-muted-foreground">
                  {{ $t('family_detail_report.revenue') }}
                </div>
              </div>
            </div>

            <!-- Market Share Card -->
            <div class="p-3 rounded-xl product-row">
              <div class="flex items-center justify-between mb-1">
                <div class="w-6 h-6 bg-emerald-500/10 rounded flex items-center justify-center">
                  <svg class="w-3 h-3 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                    <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                  </svg>
                </div>
                <div class="text-xs font-medium"
                  :class="getMarketShareEvolutionClass">
                  {{ getMarketShareEvolution }}
                </div>
              </div>
              <div>
                <div class="text-lg font-bold text-foreground">
                  {{ getMarketShare() }}
                </div>
                <div class="text-xs text-muted-foreground">
                  {{ $t('family_detail_report.market_share') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Statistiques détaillées -->
          <div class="space-y-2">
            <div class="flex justify-between items-center text-sm">
              <span class="text-muted-foreground">{{
                $t('family_detail_report.number_of_products')
              }}</span>
              <span class="font-medium">{{ familySummary.products_count }}</span>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-muted-foreground">{{
                $t('family_detail_report.total_sales') }}</span>
              <span class="font-medium">{{ familySummary.total_lines }}</span>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-muted-foreground">
                {{ $t('family_detail_report.average_basket') }}
              </span>
              <span class="font-medium">{{ getAverageBasket() }}</span>
            </div>
          </div>
        </div>
      </ReportCard>

      <!-- Tous les produits de la famille -->
      <ReportCard :title="$t('family_detail_report.family_products')">
        <div v-if="familyProducts.length > 0" class="space-y-3">
          <TransitionGroup name="product-list" tag="div" class="space-y-3">
            <div v-for="(product, index) in displayedProducts"
              :key="product.product_data.product_id"
              class="p-3 rounded-xl product-row">

              <div class="flex items-center gap-3">
                <!-- Rang -->
                <div
                  class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-semibold text-xs"
                  :class="getRankColorClass(index)">
                  {{ index + 1 }}
                </div>

                <!-- Contenu produit -->
                <div class="flex-1 min-w-0 space-y-2">
                  <!-- Header : nom + CA -->
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0 flex-1">
                      <h3 class="font-semibold text-foreground text-sm leading-tight truncate">
                        {{ product.product_data.product_name }}
                      </h3>
                      <p class="text-xs text-muted-foreground">
                        {{ $t('family_detail_report.sales',
                          { count: product.product_data.line_count }) }}
                      </p>
                    </div>

                    <div class="flex-shrink-0 text-right">
                      <div class="flex flex-col items-end">
                        <span class="font-bold text-foreground text-sm">
                          {{ formatCurrency(product.product_data.tax_data.included_tax) }}
                        </span>
                        <span v-if="product.performance.evolution"
                          class="text-xs font-medium"
                          :class="getEvolutionClass(product.performance.evolution.percentage_change[taxKey])">
                          {{ formatEvolution(product.performance.evolution.percentage_change[taxKey]) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Métriques produit -->
                  <div class="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {{ $t("family_detail_report.per_sales",
                        { avg: getProductAverageBasket(product) }) }}
                    </span>

                    <span class="flex items-center gap-1">
                      {{ $t("family_detail_report.of_total",
                        { nb: getProductMarketShare(product) }) }}
                    </span>
                  </div>

                  <!-- Barre de progression relative -->
                  <div class="flex items-center gap-2">
                    <div class="flex-1 h-1.5 progress-track rounded-full overflow-hidden">
                      <div
                        class="h-full progress-fill rounded-full transition-all duration-300"
                        :style="{ width: getProductProgressWidth(product) + '%' }">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TransitionGroup>

          <!-- Bouton voir plus/moins -->
          <div v-if="familyProducts.length > defaultLimit" class="text-center pt-3">
            <button @click="toggleExpanded"
              class="text-sm link-cyan font-medium flex items-center gap-1 mx-auto">
              <span v-if="!isExpanded">
                {{ $t("family_detail_report.see_all_products",
                  { nb: familyProducts.length }) }}
              </span>
              <span v-else>{{ $t("family_detail_report.collapse") }}</span>
              <svg class="w-4 h-4 transition-transform duration-200"
                :class="{ 'rotate-180': isExpanded }" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round"
                  stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        <div v-else class="text-center text-muted-foreground py-8">
          <p class="text-sm">{{ $t("family_detail_report.no_products_for_family") }}</p>
        </div>
      </ReportCard>

      <!-- Analyse de concentration -->
      <ReportCard :title="$t('family_detail_report.concentration_analysis')">
        <div v-if="familySummary" class="space-y-4">
          <!-- Métriques de répartition -->
          <div class="space-y-4">
            <h4 class="text-sm font-semibold text-foreground flex items-center gap-2">
              {{ $t("family_detail_report.revenue_distribution") }}
            </h4>

            <!-- Métriques textuelles -->
            <div class="space-y-2 text-sm">
              <div class="flex justify-between items-center">
                <span class="text-muted-foreground">{{
                  $t("family_detail_report.leader_product_accounts") }}</span>
                <span class="font-semibold text-foreground">
                  {{ $t("family_detail_report.percent_of_revenue",
                    { pct: getLeaderContribution() }) }}
                </span>
              </div>

              <div class="flex justify-between items-center">
                <span class="text-muted-foreground">
                  {{ $t("family_detail_report.top_3_products") }}
                </span>
                <span class="font-semibold text-blue-600">
                  {{ $t("family_detail_report.pct_share", { pct: getTop3Share() }) }}
                </span>
              </div>

              <div v-if="familyProducts.length > 3" class="flex justify-between items-center">
                <span class="text-muted-foreground">
                  {{ $t("family_detail_report.other_products",
                    { count: familyProducts.length - 3 }) }}</span>
                <span class="font-semibold text-muted-foreground">
                  {{ $t("family_detail_report.remaining_share",
                    { pct: getRemainingShare() }) }}
                </span>
              </div>
            </div>

            <!-- Jauge de répartition -->
            <div class="space-y-2">
              <div class="text-xs text-muted-foreground font-medium">
                {{ $t("family_detail_report.visual_distribution") }}
              </div>
              <div class="h-6 bg-muted rounded-lg overflow-hidden flex">
                <!-- Segment Leader -->
                <div class="h-full gauge-leader transition-all duration-500"
                  :style="{ width: getLeaderContribution() + '%' }"
                  :title="$t('family_detail_report.leading_product_contribution', { pct: getLeaderContribution() })">
                </div>

                <!-- Segment Top 2-3 -->
                <div v-if="getTop23Share() > 0"
                  class="h-full gauge-top23 transition-all duration-500"
                  :style="{ width: getTop23Share() + '%' }"
                  :title="$t('family_detail_report.products_2_3_share', { pct: getTop23Share() })">
                </div>

                <!-- Segment Autres -->
                <div v-if="getRemainingShare() > 0"
                  class="h-full gauge-others transition-all duration-500"
                  :style="{ width: getRemainingShare() + '%' }"
                  :title="$t('family_detail_report.others_remaining_share', { pct: getRemainingShare() })">
                </div>
              </div>

              <!-- Légende minimaliste -->
              <div class="flex items-center justify-between text-xs text-muted-foreground">
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 gauge-leader rounded-sm"></div>
                  <span>{{ $t("family_detail_report.leader") }}</span>
                </div>

                <div v-if="getTop23Share() > 0" class="flex items-center gap-1">
                  <div class="w-2 h-2 gauge-top23 rounded-sm"></div>
                  <span>{{ $t("family_detail_report.top_2_3") }}</span>
                </div>

                <div v-if="getRemainingShare() > 0" class="flex items-center gap-1">
                  <div class="w-2 h-2 gauge-others rounded-sm"></div>
                  <span>{{ $t("family_detail_report.others") }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ReportCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useReportContext } from '../../../composables/useReportContext'
import { useAppSettingsStore } from '../../../stores/AppSettingsStore'
import ReportCard from '../../ReportCard.vue'
import type { ProductsReportResponse, FamilySummary, EnrichedProduct } from '../../../responses'
import { formatCurrencyForCenter, formatPercentage } from '../../../utils/Languages'

const props = defineProps<{
  familyName: string
  productsReport: ProductsReportResponse | null
  centerId: number
}>()

defineEmits<{
  back: []
}>()

const { card } = useReportContext()
const appSettingsStore = useAppSettingsStore()

const center = computed(() => {
  const c = appSettingsStore.getCenter(props.centerId)
  if (!c) {
    throw new Error('[FamilyDetailInline] No center found')
  }
  return c
})

// Expansion state
const isExpanded = ref(false)
const defaultLimit = 5

const familySummary = computed((): FamilySummary | null => {
  if (!props.productsReport?.families_summary) return null
  return props.productsReport.families_summary.find(f => f.family_name === props.familyName) || null
})

const taxKey = computed(() => {
  return card.value?.getTaxKey() || 'excluded_tax'
})

const familyProducts = computed((): EnrichedProduct[] => {
  if (!props.productsReport?.products) return []
  return props.productsReport.products
    .filter(p => p.product_data.family_name === props.familyName)
    .sort((a, b) => {
      const revenueDiff = b.product_data.tax_data.included_tax - a.product_data.tax_data.included_tax
      if (revenueDiff !== 0) return revenueDiff
      return b.product_data.line_count - a.product_data.line_count
    })
})

const displayedProducts = computed(() => {
  if (isExpanded.value || familyProducts.value.length <= defaultLimit) {
    return familyProducts.value
  }
  return familyProducts.value.slice(0, defaultLimit)
})

const maxProductRevenue = computed(() => {
  if (familyProducts.value.length === 0) return 0
  return Math.max(...familyProducts.value.map(p => p.product_data.tax_data.included_tax))
})

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

const getMarketShare = (): string => {
  if (!familySummary.value || !props.productsReport?.period_summary?.total_revenue) return '0%'
  const share = (familySummary.value.tax_data[taxKey.value] / props.productsReport.period_summary.total_revenue[taxKey.value]) * 100
  return `${Math.round(share)}%`
}

const getMarketShareEvolution = computed((): string => {
  if (!familySummary.value || !props.productsReport?.period_summary?.evolution) return ''
  const currentShare = (familySummary.value.tax_data[taxKey.value] / props.productsReport.period_summary.total_revenue[taxKey.value]) * 100
  const previousShare = (familySummary.value.comparison.previous_tax_data[taxKey.value] / props.productsReport.period_summary.evolution.previous_total_revenue[taxKey.value]) * 100
  const evolution = currentShare - previousShare
  const sign = evolution > 0 ? '+' : ''
  return `${sign}${evolution.toFixed(1)}pts`
})

const getMarketShareEvolutionClass = computed((): string => {
  if (!familySummary.value?.comparison || !props.productsReport?.period_summary?.evolution?.previous_total_revenue) return 'text-muted-foreground'

  const currentShare = (familySummary.value.tax_data[taxKey.value] / props.productsReport.period_summary.total_revenue[taxKey.value]) * 100
  const previousShare = (familySummary.value.comparison.previous_tax_data[taxKey.value] / props.productsReport.period_summary.evolution.previous_total_revenue[taxKey.value]) * 100

  const evolution = currentShare - previousShare

  if (evolution > 0) return 'text-green-600'
  if (evolution < 0) return 'text-red-600'
  return 'text-muted-foreground'
})

const getRevenueEvolution = (): string => {
  if (!familySummary.value?.comparison) return ''
  const evolutionPct = familySummary.value.comparison.tax_data_change_pct[taxKey.value]
  const sign = evolutionPct > 0 ? '+' : ''
  return `${sign}${evolutionPct.toFixed(1)}%`
}

const getRevenueEvolutionClass = (): string => {
  if (!familySummary.value?.comparison) return 'text-muted-foreground'
  const evolutionPct = familySummary.value.comparison.tax_data_change_pct[taxKey.value]
  if (evolutionPct > 0) return 'text-green-600'
  if (evolutionPct < 0) return 'text-red-600'
  return 'text-muted-foreground'
}

const getAverageBasket = (): string => {
  if (!familySummary.value || familySummary.value.total_lines === 0) return formatCurrency(0)
  const avg = familySummary.value.tax_data[taxKey.value] / familySummary.value.total_lines
  return formatCurrency(avg)
}

const getLeaderContribution = (): number => {
  if (!familySummary.value || familySummary.value.tax_data[taxKey.value] === 0) return 0
  return Math.round((familySummary.value.top_product.total_ati / familySummary.value.tax_data[taxKey.value]) * 100)
}

const getProductAverageBasket = (product: EnrichedProduct): string => {
  if (product.product_data.line_count === 0) return formatCurrency(0)
  const avg = product.product_data.tax_data.included_tax / product.product_data.line_count
  return formatCurrency(avg)
}

const getProductMarketShare = (product: EnrichedProduct): string => {
  if (!props.productsReport?.period_summary?.total_revenue) return formatPercentage(0)
  const share = (product.product_data.tax_data.included_tax / props.productsReport.period_summary.total_revenue[taxKey.value]) * 100
  return formatPercentage(Math.round(share) / 100)
}

const getProductProgressWidth = (product: EnrichedProduct): number => {
  if (maxProductRevenue.value === 0) return 0
  return Math.round((product.product_data.tax_data.included_tax / maxProductRevenue.value) * 100)
}

const getTop3Share = (): number => {
  if (!familySummary.value || familyProducts.value.length === 0) return 0
  const top3Total = familyProducts.value.slice(0, 3).reduce((sum, product) =>
    sum + product.product_data.tax_data.included_tax, 0
  )
  const top3Share = (top3Total / familySummary.value.tax_data[taxKey.value]) * 100
  return Math.round(top3Share)
}

const getTop23Share = (): number => {
  if (!familySummary.value || familyProducts.value.length < 2) return 0
  const top23Total = familyProducts.value.slice(1, 3).reduce((sum, product) =>
    sum + product.product_data.tax_data.included_tax, 0
  )
  const top23Share = (top23Total / familySummary.value.tax_data[taxKey.value]) * 100
  return Math.round(top23Share)
}

const getRemainingShare = (): number => {
  if (!familySummary.value || familyProducts.value.length <= 3) return 0
  const top3Total = familyProducts.value.slice(0, 3).reduce((sum, product) =>
    sum + product.product_data.tax_data.included_tax, 0
  )
  const remaining = familySummary.value.tax_data[taxKey.value] - top3Total
  const remainingShare = (remaining / familySummary.value.tax_data[taxKey.value]) * 100
  return Math.round(remainingShare)
}

const getRankColorClass = (index: number): string => {
  const classes = ['rank-1', 'rank-2', 'rank-3', 'rank-default', 'rank-default']
  return classes[index] || 'rank-default'
}

const formatEvolution = (percentage: number): string => {
  const sign = percentage > 0 ? '+' : ''
  return `${sign}${percentage.toFixed(1)}%`
}

const getEvolutionClass = (percentage: number): string => {
  if (percentage > 0) return 'text-green-600'
  if (percentage < 0) return 'text-red-600'
  return 'text-muted-foreground'
}

const formatCurrency = (amount: number): string => {
  return formatCurrencyForCenter(amount, center.value)
}
</script>

<style scoped>
/* Animations pour l'expansion de la liste des produits */
.product-list-enter-active,
.product-list-leave-active {
  transition: all 0.3s ease;
}

.product-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
  overflow: hidden;
}

.product-list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
  overflow: hidden;
}

.product-list-move {
  transition: transform 0.3s ease;
}

.product-list-enter-active {
  animation: slideDown 0.3s ease forwards;
}

.product-list-leave-active {
  animation: slideUp 0.3s ease forwards;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  to {
    opacity: 1;
    max-height: 200px;
    margin-bottom: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    max-height: 200px;
    margin-bottom: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
  to {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
}

/* Product row containers & overview mini-cards */
.product-row {
  border: 1px solid var(--border-medium);
  border-radius: var(--radius-card);
  transition: background 0.15s ease;
}

.product-row:hover {
  background: rgba(0, 0, 0, 0.02);
}

:is(.dark *).product-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

/* Rank badges */
.rank-1 {
  background: var(--cyan-subtle);
  color: var(--apex-cyan);
  border: 1px solid var(--cyan-border);
}

.rank-2 {
  background: rgba(148, 163, 184, 0.25);
  color: var(--foreground);
  border: 1px solid rgba(148, 163, 184, 0.35);
}

.rank-3 {
  background: rgba(180, 130, 80, 0.10);
  color: rgb(180, 130, 80);
  border: 1px solid rgba(180, 130, 80, 0.15);
}

:is(.dark *).rank-3 {
  color: rgb(210, 165, 110);
}

.rank-default {
  background: rgba(148, 163, 184, 0.10);
  color: var(--foreground);
  border: 1px solid rgba(148, 163, 184, 0.20);
}

/* Progress bars */
.progress-track {
  background: var(--border-subtle);
}

.progress-fill {
  background: var(--apex-cyan);
}

/* Link & icon colors */
.link-cyan {
  color: var(--apex-cyan);
  transition: opacity 0.15s ease;
}

.link-cyan:hover {
  opacity: 0.75;
}

.icon-cyan {
  color: var(--apex-cyan);
}

/* Concentration gauge */
.gauge-leader { background: #F59E0B; }
.gauge-top23 { background: #8B5CF6; }
.gauge-others { background: var(--muted-foreground); opacity: 0.35; }
</style>
